import React, { useMemo, useState } from 'react';
import ThreadChat from '../../components/forms/outreach/ThreadChat';
import { OutreachAction } from '../../components/forms/outreach/types';
import { actionsForThread, buildActionIndex, composeAskQuery } from '../../hooks/outreachActionModel';
import { ACTIONS, THREADS } from './outreachFixtures';
import { LONG_THREAD } from './outreachLongThread';

/**
 * The moves bar, in the four states that decide whether the rework worked:
 * collapsed with one ask already in the box, the counter's confirm step open
 * over the conversation, the detail sheet open over it, and a long thread where
 * the messages have to dominate the pane and the change ribbon has to scroll
 * away with the reply that caused it.
 *
 * Every pane is a real `ThreadChat` at a realistic height — the point is the
 * proportion between the conversation and everything else, which a component
 * shown on its own would hide.
 */
const noop = () => undefined;

const Pane: React.FC<{ note: string; children: React.ReactNode; height: string; width?: string }> = ({
  note,
  children,
  height,
  width = '',
}) => (
  <div className="flex flex-col gap-s2">
    <p className="m-0 text-meta text-ink-secondary">{note}</p>
    {/* `flex` so the pane's height reaches the chat: a block child would grow
        to its content and be clipped instead of laying itself out inside. */}
    <div className={`${height} ${width} flex overflow-hidden rounded-card border border-line bg-surface`}>
      {children}
    </div>
  </div>
);

const OutreachMovesGallery: React.FC = () => {
  const index = useMemo(() => buildActionIndex(ACTIONS), []);
  const kaisen = THREADS[0];
  const kaisenActions = useMemo(() => actionsForThread(index, kaisen), [index, kaisen]);
  const longActions = useMemo(() => actionsForThread(index, LONG_THREAD), [index]);

  // The collapsed pane stages for real, and opens with one ask already in the
  // box so the subdued pill is on screen without a click.
  const [staged, setStaged] = useState<string[]>(['a3']);
  const [text, setText] = useState('3. We will take the 120-unit release at week 10.');

  const stageIntoComposer = (chosen: OutreachAction[], approvedPrice?: number) => {
    const fresh = chosen.filter((action) => !staged.includes(action.id));
    if (fresh.length === 0) return;
    const query = composeAskQuery(kaisen, fresh, approvedPrice, staged.length);
    setText((prev) => (prev.trim() ? `${prev.trimEnd()}\n\n${query}` : query));
    setStaged((prev) => [...prev, ...fresh.map((action) => action.id)]);
  };

  const chat = (over: Partial<React.ComponentProps<typeof ThreadChat>>) => (
    <ThreadChat
      activeThread={kaisen}
      actions={kaisenActions}
      messageInput=""
      onMessageInputChange={noop}
      onSendMessage={noop}
      onStageActions={noop}
      stagedActionIds={staged}
      onDismissAction={noop}
      isSending={false}
      sendError={null}
      onDismissSendError={noop}
      onSaveContactEmail={async () => false}
      isSavingEmail={false}
      emailSaveError={null}
      onDiscardThread={noop}
      isDiscardingThread={false}
      {...over}
    />
  );

  return (
    <div className="flex flex-col gap-s5">
      <Pane
        height="h-[560px]"
        note="Collapsed: one row above the composer — Robbie, the move count, a pill per open move, the bundle, and the chevron. The third ask is already in the box, so its pill is subdued and says so. Every pill carries its title, its reasoning and the exact ask in an instant tooltip; the conversation keeps the rest of the pane."
      >
        {chat({ messageInput: text, onMessageInputChange: setText, onStageActions: stageIntoComposer })}
      </Pane>

      <Pane
        height="h-[560px]"
        width="max-w-[660px]"
        note="The same bar with less room: the moves that no longer fit the one line are clipped and counted on the “+N” pill, whose tooltip names them. They are not lost — the chevron's sheet carries every move, fitted or not."
      >
        {chat({})}
      </Pane>

      <Pane
        height="h-[560px]"
        note="Counter pill tapped: the confirm step opens upward OVER the conversation — editable number, the working, the floor line, confirm, then accept. Nothing below it moved. Escape, a click outside, or the pill again closes it, and an unconfirmed number still cannot reach the box."
      >
        {chat({ initialOverlay: 'counter' })}
      </Pane>

      <Pane
        height="h-[580px]"
        note="Chevron tapped: the full detail sheet lifts over the lower conversation — every move with its reasoning, its working, its own “Put in the box” and its “Not now”, including any move that did not fit on the bar's one line."
      >
        {chat({ initialOverlay: 'sheet' })}
      </Pane>

      <Pane
        height="h-[620px]"
        note="Fourteen messages, one bar: the messages own the pane and the TERMS MOVED ribbon sits inside the scroll, under the reply that moved them — scroll up and it leaves with that reply instead of hovering above the composer. Its long payment-terms values, its two commitment chips and its trailing condition all clip on the line and read in full on hover."
      >
        <ThreadChat
          activeThread={LONG_THREAD}
          actions={longActions}
          messageInput=""
          onMessageInputChange={noop}
          onSendMessage={noop}
          onStageActions={noop}
          stagedActionIds={[]}
          onDismissAction={noop}
          isSending={false}
          sendError={null}
          onDismissSendError={noop}
          onSaveContactEmail={async () => false}
          isSavingEmail={false}
          emailSaveError={null}
          onDiscardThread={noop}
          isDiscardingThread={false}
        />
      </Pane>
    </div>
  );
};

export default OutreachMovesGallery;
